import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import '../css/RecurringPreview.css';

function RecurringPreview({ dates, scheduleType, scheduledTime, startTimeOfDay, subjectColor, title, excludedDates = [], onExcludeToggle, onTimeChange, onClose }) {
    const { t } = useTranslation();

    // Parse dates into Date objects for calendar rendering
    const dateSet = useMemo(() => new Set(dates), [dates]);
    const excludedSet = useMemo(() => new Set(excludedDates), [excludedDates]);

    // Determine initial month from first date
    const firstDate = dates.length > 0 ? new Date(dates[0]) : new Date();
    const [currentMonth, setCurrentMonth] = useState({
        year: firstDate.getFullYear(),
        month: firstDate.getMonth()
    });

    // Time state for drag adjustment
    const [startTime, setStartTime] = useState(startTimeOfDay || '09:00');
    const [duration, setDuration] = useState(scheduledTime || 60);

    // Generate calendar grid for current month
    const calendarDays = useMemo(() => {
        const { year, month } = currentMonth;
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startDayOfWeek = firstDay.getDay();

        const days = [];

        // Previous month days
        const prevMonthLast = new Date(year, month, 0);
        for (let i = startDayOfWeek - 1; i >= 0; i--) {
            const d = prevMonthLast.getDate() - i;
            // Approximate - prev month
            const pm = month === 0 ? 11 : month - 1;
            const py = month === 0 ? year - 1 : year;
            const pDateStr = `${py}-${String(pm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            days.push({ day: d, dateStr: pDateStr, otherMonth: true });
        }

        // Current month days
        for (let d = 1; d <= lastDay.getDate(); d++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            days.push({ day: d, dateStr, otherMonth: false });
        }

        // Next month days to fill grid
        const remaining = 42 - days.length; // 6 rows * 7 cols
        for (let d = 1; d <= remaining; d++) {
            const nm = month === 11 ? 0 : month + 1;
            const ny = month === 11 ? year + 1 : year;
            const dateStr = `${ny}-${String(nm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            days.push({ day: d, dateStr, otherMonth: true });
        }

        return days;
    }, [currentMonth]);

    const dayHeaders = useMemo(() => {
        const keys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return keys.map(k => t(k).charAt(0));
    }, [t]);

    const handleDateClick = useCallback((dateStr, otherMonth) => {
        if (otherMonth) return;
        if (!dateSet.has(dateStr) && !excludedSet.has(dateStr)) return;
        if (onExcludeToggle) onExcludeToggle(dateStr);
    }, [dateSet, excludedSet, onExcludeToggle]);

    const handlePrevMonth = () => {
        setCurrentMonth(prev => {
            if (prev.month === 0) return { year: prev.year - 1, month: 11 };
            return { ...prev, month: prev.month - 1 };
        });
    };

    const handleNextMonth = () => {
        setCurrentMonth(prev => {
            if (prev.month === 11) return { year: prev.year + 1, month: 0 };
            return { ...prev, month: prev.month + 1 };
        });
    };

    const parseTime = (timeStr) => {
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
    };

    const startMinutes = parseTime(startTime);
    const stripStartHour = Math.max(0, Math.floor(startMinutes / 60) - 2);
    const stripEndHour = Math.min(24, Math.ceil((startMinutes + duration) / 60) + 2);
    const stripRange = stripEndHour - stripStartHour;
    const blockTop = ((startMinutes - stripStartHour * 60) / (stripRange * 60)) * 200;
    const blockHeight = (duration / (stripRange * 60)) * 200;

    // Count stats
    const activeDates = dates.filter(d => !excludedSet.has(d));
    const lastDate = dates.length > 0 ? dates[dates.length - 1] : '';
    const firstDateStr = dates.length > 0 ? dates[0] : '';

    return (
        <div className="recurring-preview-overlay" onClick={onClose}>
            <div className="recurring-preview-dialog" onClick={e => e.stopPropagation()}>
                <h3>{t('recurring.preview.title')}</h3>

                {/* Mini Calendar */}
                <div className="recurring-mini-calendar">
                    <div className="recurring-mini-month-header">
                        <button className="recurring-mini-nav-btn" onClick={handlePrevMonth}>&#9664;</button>
                        <span>{currentMonth.year}년 {currentMonth.month + 1}월</span>
                        <button className="recurring-mini-nav-btn" onClick={handleNextMonth}>&#9654;</button>
                    </div>
                    <div className="recurring-mini-grid">
                        {dayHeaders.map((d, i) => (
                            <div key={i} className="recurring-mini-day-header">{d}</div>
                        ))}
                        {calendarDays.map((day, i) => {
                            const isScheduled = dateSet.has(day.dateStr);
                            const isExcluded = excludedSet.has(day.dateStr);
                            let className = 'recurring-mini-day';
                            if (day.otherMonth) className += ' other-month';
                            if (isExcluded) className += ' excluded';
                            else if (isScheduled && scheduleType === 'timed') className += ' has-schedule';
                            else if (isScheduled && scheduleType === 'todo') className += ' has-todo';

                            return (
                                <div
                                    key={i}
                                    className={className}
                                    onClick={() => handleDateClick(day.dateStr, day.otherMonth)}
                                    title={isExcluded ? t('recurring.preview.excludedDate') : ''}
                                >
                                    {day.day}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Legend */}
                <div className="recurring-preview-legend">
                    {scheduleType === 'timed' ? (
                        <div className="recurring-preview-legend-item">
                            <div className="recurring-preview-legend-bar"></div>
                            <span>{t('recurring.type.schedule')}</span>
                        </div>
                    ) : (
                        <div className="recurring-preview-legend-item">
                            <div className="recurring-preview-legend-dot"></div>
                            <span>{t('recurring.type.todo')}</span>
                        </div>
                    )}
                    <div className="recurring-preview-legend-item">
                        <div className="recurring-preview-legend-excluded">X</div>
                        <span>{t('recurring.preview.excludedDate')}</span>
                    </div>
                </div>

                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    {t('recurring.preview.clickToExclude')}
                </p>

                {/* Info */}
                <div className="recurring-preview-info">
                    <p><strong>{t('recurring.preview.totalOccurrences', { count: activeDates.length })}</strong></p>
                    {firstDateStr && lastDate && (
                        <p>{t('recurring.preview.period', { start: firstDateStr, end: lastDate })}</p>
                    )}
                </div>

                {/* Time Adjust (timed only) */}
                {scheduleType === 'timed' && (
                    <div className="recurring-time-adjust">
                        <div className="recurring-time-adjust-title">{t('recurring.preview.timeAdjust')}</div>
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                            <label style={{ fontSize: '13px' }}>
                                {t('recurring.form.startTime')}:
                                <input
                                    type="time"
                                    value={startTime}
                                    onChange={e => setStartTime(e.target.value)}
                                    style={{ marginLeft: '6px', padding: '4px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                                />
                            </label>
                            <label style={{ fontSize: '13px' }}>
                                {t('recurring.form.duration')}:
                                <input
                                    type="number"
                                    value={duration}
                                    onChange={e => setDuration(Math.max(1, parseInt(e.target.value) || 0))}
                                    min="1"
                                    style={{ marginLeft: '6px', width: '60px', padding: '4px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                                />
                            </label>
                        </div>
                        <div className="recurring-time-strip">
                            {Array.from({ length: stripRange + 1 }, (_, i) => stripStartHour + i).filter(h => h <= 24).map(h => (
                                <div
                                    key={h}
                                    className="recurring-time-label"
                                    style={{ top: `${((h - stripStartHour) / stripRange) * 200}px` }}
                                >
                                    {String(h).padStart(2, '0')}:00
                                </div>
                            ))}
                            <div
                                className="recurring-time-block"
                                style={{
                                    top: `${Math.max(0, blockTop)}px`,
                                    height: `${Math.max(20, blockHeight)}px`,
                                    backgroundColor: subjectColor || '#2196f3',
                                    color: '#fff',
                                }}
                            >
                                {title}
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="recurring-preview-actions">
                    <button className="btn-secondary" onClick={onClose}>
                        {t('close')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default RecurringPreview;
